import { useEffect, useRef, useState } from "preact/hooks";
import { Button, Chip } from "../components";
import { ArticulatorDiagram } from "./library/ArticulatorDiagram";
import type { TimeOfDay } from "../state/profile";
import type { Lang } from "../content/types";
import { GOAL_LABEL, type Goal } from "../content/copy";
import { isMicSupported, requestMic, type MicSession } from "../audio/mic";
import { downloadIcs } from "../lib/ics";
import {
  completeSession,
  markOnboarded,
  recordPhonationPB,
  setIdentityGoal,
  setIdentityLangs,
  setIdentityWhen,
  setMicEnabled,
  setWeekTarget,
} from "../state/store";

const GOALS: Goal[] = ["stage", "meetings", "language", "clarity"];
const GOAL_DIAGRAM: Record<Goal, string> = {
  stage: "tip-alveolar",
  meetings: "lips",
  language: "vowel-round",
  clarity: "palatal",
};

const FIRST_REP_BONUS = 150; // guarantees Rank 2 (Murmur, 150 Clarity) right out of onboarding

export function Onboarding({ onDone }: { onDone: () => void }) {
  const [step, setStep] = useState(1);
  const [goal, setGoal] = useState<Goal | null>(null);
  const [langs, setLangs] = useState<Lang[]>(["en"]);
  const [when, setWhen] = useState<TimeOfDay | null>(null);
  const [weekTarget, setLocalWeekTarget] = useState<3 | 5 | 7>(5);

  function next() {
    setStep((s) => s + 1);
  }

  return (
    <div class="onboarding" role="dialog" aria-modal="true" aria-label="Welcome to Diction Dojo">
      {step === 1 && <ColdOpen onDone={next} />}
      {step === 2 && (
        <WhyHere
          goal={goal}
          onPick={(g) => {
            setGoal(g);
            setIdentityGoal(g);
            next();
          }}
        />
      )}
      {step === 3 && (
        <Languages
          langs={langs}
          onNext={(l) => {
            setLangs(l);
            setIdentityLangs(l);
            next();
          }}
        />
      )}
      {step === 4 && (
        <Schedule
          when={when}
          weekTarget={weekTarget}
          onNext={(w, target) => {
            setWhen(w);
            setLocalWeekTarget(target);
            if (w) setIdentityWhen(w);
            setWeekTarget(target);
            next();
          }}
        />
      )}
      {step === 5 && <FirstRep onDone={onDone} />}
    </div>
  );
}

function ColdOpen({ onDone }: { onDone: () => void }) {
  const [line, setLine] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setLine(1), 900);
    return () => clearTimeout(t);
  }, []);
  return (
    <div class="onboard-step onboard-cold-open">
      <p class="display onboard-cold-line">{line === 0 ? "Your mouth is a muscle." : "Five minutes a day."}</p>
      {line === 1 && (
        <Button size="lg" onClick={onDone}>
          Begin
        </Button>
      )}
    </div>
  );
}

function WhyHere({ goal, onPick }: { goal: Goal | null; onPick: (g: Goal) => void }) {
  return (
    <div class="onboard-step">
      <p class="stage-kicker">Why are you here?</p>
      <div class="onboard-grid">
        {GOALS.map((g) => (
          <button key={g} type="button" class={"onboard-tile" + (goal === g ? " onboard-tile-active" : "")} onClick={() => onPick(g)}>
            <ArticulatorDiagram id={GOAL_DIAGRAM[g]} size={56} />
            <span class="display onboard-tile-label">{GOAL_LABEL[g]}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Languages({ langs, onNext }: { langs: Lang[]; onNext: (langs: Lang[]) => void }) {
  const [selected, setSelected] = useState<Lang[]>(langs);
  function toggle(l: Lang) {
    setSelected((s) => (s.includes(l) ? s.filter((x) => x !== l) : [...s, l]));
  }
  return (
    <div class="onboard-step">
      <p class="stage-kicker">Which languages?</p>
      <div class="onboard-chip-row">
        <Chip active={selected.includes("en")} onClick={() => toggle("en")}>
          English
        </Chip>
        <Chip active={selected.includes("sv")} onClick={() => toggle("sv")}>
          Svenska
        </Chip>
      </div>
      <Button size="lg" onClick={() => onNext(selected.length ? selected : ["en"])}>
        Continue
      </Button>
    </div>
  );
}

function Schedule({
  when,
  weekTarget,
  onNext,
}: {
  when: TimeOfDay | null;
  weekTarget: 3 | 5 | 7;
  onNext: (when: TimeOfDay | null, target: 3 | 5 | 7) => void;
}) {
  const [localWhen, setLocalWhen] = useState<TimeOfDay | null>(when);
  const [target, setTarget] = useState<3 | 5 | 7>(weekTarget);

  return (
    <div class="onboard-step">
      <p class="stage-kicker">When do you train, and how often?</p>
      <div class="onboard-chip-row">
        {(["morning", "midday", "evening"] as TimeOfDay[]).map((w) => (
          <Chip key={w} active={localWhen === w} onClick={() => setLocalWhen(w)}>
            {w[0]!.toUpperCase() + w.slice(1)}
          </Chip>
        ))}
      </div>
      <div class="onboard-chip-row">
        {[3, 5, 7].map((n) => (
          <Chip key={n} active={target === n} onClick={() => setTarget(n as 3 | 5 | 7)}>
            {n} days/week
          </Chip>
        ))}
      </div>
      <Button
        variant="ghost"
        onClick={() =>
          downloadIcs({ title: "Diction Dojo — 5 minute practice", description: "Your daily articulation session.", time: "08:00" })
        }
      >
        Book your 5 minutes (calendar reminder)
      </Button>
      <Button size="lg" onClick={() => onNext(localWhen, target)}>
        Continue
      </Button>
    </div>
  );
}

function FirstRep({ onDone }: { onDone: () => void }) {
  const [phase, setPhase] = useState<"intro" | "consent" | "counting" | "hold" | "result">("intro");
  const [seconds, setSeconds] = useState(0);
  const micRef = useRef<MicSession | null>(null);
  const startRef = useRef(0);
  const [n, setN] = useState(3);

  useEffect(() => {
    if (phase !== "counting") return;
    if (n <= 0) {
      setPhase("hold");
      return;
    }
    const t = setTimeout(() => setN((x) => x - 1), 700);
    return () => clearTimeout(t);
  }, [phase, n]);

  useEffect(() => {
    if (phase !== "hold") return;
    startRef.current = Date.now();
    const cap = 40000;
    const tick = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      setSeconds(Math.min(40, Math.floor(elapsed / 1000)));
      if (elapsed >= cap) finish();
    }, 100);
    return () => clearInterval(tick);
    // eslint-disable-next-line
  }, [phase]);

  function finish() {
    micRef.current?.stop();
    const dur = Math.max(1, Math.round((Date.now() - startRef.current) / 1000));
    setSeconds(dur);
    recordPhonationPB(dur);
    completeSession({
      closesRing: false,
      clarityAwarded: FIRST_REP_BONUS,
      secondsSpent: dur,
      ctx: { sessionHour: new Date().getHours() },
    });
    // markOnboarded() is deliberately deferred to the "Enter the dojo" tap —
    // Root swaps to <App/> the instant it flips, so firing it here would
    // skip the result screen entirely.
    setPhase("result");
  }

  async function startWithMic() {
    setPhase("consent");
    if (isMicSupported()) {
      try {
        micRef.current = await requestMic();
        setMicEnabled(true);
      } catch {
        /* denied or unavailable — the manual "stop" button still works */
      }
    }
    setPhase("counting");
  }

  if (phase === "intro" || phase === "consent") {
    return (
      <div class="onboard-step">
        <p class="stage-kicker">First rep, right now</p>
        <p class="display onboard-cold-line">Take one deep breath, then say "AAAH" for as long as you can.</p>
        <p class="stage-detail">
          If you allow the mic, it's analyzed on this device only — never recorded, stored, or sent anywhere.
        </p>
        <Button size="lg" onClick={startWithMic}>
          {phase === "consent" ? "Asking…" : "I'm ready"}
        </Button>
      </div>
    );
  }

  if (phase === "counting") {
    return (
      <div class="onboard-step">
        <p class="stage-kicker">Get ready</p>
        <div class="display mono stage-hero">{n > 0 ? n : "GO"}</div>
      </div>
    );
  }

  if (phase === "hold") {
    return (
      <div class="onboard-step">
        <p class="stage-kicker">Hold it — "AAAH"</p>
        <div class="mono display stage-hero">{seconds}s</div>
        <Button size="lg" variant="ghost" onClick={finish}>
          I'm out of air
        </Button>
      </div>
    );
  }

  return (
    <div class="onboard-step">
      <p class="stage-kicker">That's your baseline</p>
      <div class="display stage-hero-sm">{seconds} seconds</div>
      <p class="stage-detail">First Clarity earned, first personal best, Rank 2 reached. Your streak starts today.</p>
      <Button
        size="lg"
        onClick={() => {
          markOnboarded();
          onDone();
        }}
      >
        Enter the dojo
      </Button>
    </div>
  );
}
